import React from 'react';
import { Artifact } from '../../types';
import MarkdownRenderer from './MarkdownRenderer';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Image, Code, Table, FileCode, ExternalLink } from 'lucide-react';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const ChartArtifact: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
  const chartType = data.chartType || 'bar';
  const chartData = data.data || [];
  const dataKeys = data.dataKeys || Object.keys(chartData[0] || {}).filter((k) => k !== 'name' && k !== 'label');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 my-2">
      {title && <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>}
      <ResponsiveContainer width="100%" height={200}>
        {chartType === 'line' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            {dataKeys.map((key: string, i: number) => (
              <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} />
            ))}
          </LineChart>
        ) : chartType === 'pie' ? (
          <PieChart>
            <Pie data={chartData} dataKey={dataKeys[0] || 'value'} nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {chartData.map((_: any, i: number) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        ) : (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            {dataKeys.map((key: string, i: number) => (
              <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const ImageArtifact: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
  const src = typeof data === 'string' ? data : data.url || data.src;
  const alt = title || data.alt || 'Image';

  return (
    <div className="my-2">
      {title && <h4 className="text-sm font-medium text-gray-700 mb-1">{title}</h4>}
      <img
        src={src}
        alt={alt}
        className="max-w-full rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => window.open(src, '_blank')}
      />
    </div>
  );
};

const HtmlArtifact: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
  const html = typeof data === 'string' ? data : data.html || data.content || '';

  return (
    <div className="my-2">
      {title && (
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-medium text-gray-700">{title}</h4>
          <ExternalLink className="h-3 w-3 text-gray-400" />
        </div>
      )}
      <iframe
        srcDoc={html}
        title={title || 'HTML Content'}
        className="w-full min-h-[200px] max-h-[500px] rounded-lg border border-gray-200 bg-white"
        sandbox="allow-scripts"
        style={{ resize: 'vertical' }}
      />
    </div>
  );
};

const TableArtifact: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
  const headers: string[] = data.headers || (data.rows?.[0] ? Object.keys(data.rows[0]) : []);
  const rows: any[] = data.rows || [];

  return (
    <div className="my-2 overflow-x-auto">
      {title && <h4 className="text-sm font-medium text-gray-700 mb-1">{title}</h4>}
      <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg text-sm">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 text-right font-medium text-gray-600">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50 transition-colors">
              {headers.map((h, j) => (
                <td key={j} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                  {Array.isArray(row) ? row[j] : row[h]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CodeArtifact: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
  const code = typeof data === 'string' ? data : data.code || data.content || '';
  const language = typeof data === 'object' ? data.language : '';

  return (
    <div className="my-2">
      {title && (
        <div className="flex items-center gap-2 mb-1">
          <FileCode className="h-4 w-4 text-gray-400" />
          <h4 className="text-sm font-medium text-gray-700">{title}</h4>
          {language && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{language}</span>}
        </div>
      )}
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const ArtifactIcon: React.FC<{ type: string }> = ({ type }) => {
  const cls = 'h-3.5 w-3.5';
  switch (type) {
    case 'image': return <Image className={cls} />;
    case 'chart': return <BarChart className={cls} />;
    case 'code': return <Code className={cls} />;
    case 'table': return <Table className={cls} />;
    default: return <FileCode className={cls} />;
  }
};

const ArtifactRenderer: React.FC<{ artifact: Artifact }> = ({ artifact }) => {
  switch (artifact.type) {
    case 'image':
      return <ImageArtifact data={artifact.data} title={artifact.title} />;
    case 'chart':
      return <ChartArtifact data={artifact.data} title={artifact.title} />;
    case 'html':
      return <HtmlArtifact data={artifact.data} title={artifact.title} />;
    case 'table':
      return <TableArtifact data={artifact.data} title={artifact.title} />;
    case 'code':
      return <CodeArtifact data={artifact.data} title={artifact.title} />;
    case 'markdown':
      return (
        <div className="my-2">
          {artifact.title && <h4 className="text-sm font-medium text-gray-700 mb-1">{artifact.title}</h4>}
          <MarkdownRenderer content={typeof artifact.data === 'string' ? artifact.data : artifact.data?.content || ''} />
        </div>
      );
    default:
      return (
        <div className="my-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
          <div className="flex items-center gap-2 mb-1">
            <ArtifactIcon type={artifact.type} />
            <span>Artifact: {artifact.type}</span>
          </div>
          <pre className="text-xs overflow-x-auto">{JSON.stringify(artifact.data, null, 2)}</pre>
        </div>
      );
  }
};

export default ArtifactRenderer;
